import { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import { ApolloError } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useFlags } from 'launchdarkly-react-client-sdk';
import ColumnSortTable from 'components/Table/ColumnSortTable';
import Table, { TableColumnType, TableRow } from '../Table';
import { buildTableRow } from '../../views/Portfolio/helpers';
import { WineCardListSkeleton } from '../Skeleton';
import DetailCard from '../../views/Portfolio/components/Details/CardView';
import ProductSlideout from '../ProductTemplates/components/ProductSlideout';
import SortAndFilterLayout, {
  SortAndFilterLayoutContext,
  SortAndFilterLayoutContextType,
  TabState,
} from '../Layout/SortAndFilterLayout';
import SharedTemplate from '../ProductTemplates/components/MessageTemplate';
import noResultImage from '../../assets/images/no_results_image.png';
import { BaseProduct, Product } from '../../types/productType';
import { SortByOption, SortByType, StockTotal } from '../../views/Portfolio/types';
import { DDFilterItem, FilterTypes } from '../Filters';

enum NoResultsTextKeys {
  TITLE = 'title',
  SUBTITLE = 'subTitle',
}

const dateFormat = 'DD MMM YYYY';

interface StockTabProps {
  datasource: Product[];
  loading: boolean;
  error?: Error | null;
  id?: string;
  onStateChange?: (tabState: TabState | null) => void;
  tabState?: TabState | null;
  disableTableClick?: boolean;
  columns?: TableColumnType[];
  sortByOptions?: SortByOption[];
  defaultSortBy?: SortByType;
  refreshTotals?: (tableData: Product[]) => StockTotal[];
  children?: (context: SortAndFilterLayoutContextType | undefined) => ReactNode;
  filterPanelContainerClassName?: string;
  isCardViewDefault?: boolean;
  showCompactSwitch?: boolean;
  moreSelectorTemplate?: () => ReactNode;
  filterConfigure?: (filters: DDFilterItem[]) => DDFilterItem[];
  filterOverrides?: Partial<Record<FilterTypes, unknown>>;
  searchQueryIn?: string;
  disableSort?: boolean;
  defaultSortKey?: string;
  disableExport?: boolean;
  selectedPortfolioId?: number | string | null;
  sortIsAsc?: boolean;
  /** Total count for server-side pagination */
  totalCount?: number;
  /** Bound the table in its own scroll box with a pinned header. */
  scrollContained?: boolean;
  /** Pin the first (wine name) column while scrolling horizontally. */
  stickyFirstColumn?: boolean;
  /** Extra control rendered in the toolbar (e.g. a per-case / totals view toggle). */
  toolbarExtra?: ReactNode;
}

const StockTab: FC<StockTabProps> = ({
  datasource,
  loading,
  error,
  id,
  onStateChange,
  tabState,
  disableTableClick = false,
  columns,
  sortByOptions,
  defaultSortBy,
  refreshTotals,
  children,
  filterPanelContainerClassName,
  isCardViewDefault = false,
  showCompactSwitch = false,
  moreSelectorTemplate = null,
  filterConfigure,
  filterOverrides,
  searchQueryIn = '',
  disableSort,
  defaultSortKey = '',
  disableExport,
  selectedPortfolioId,
  sortIsAsc,
  totalCount,
  scrollContained = false,
  stickyFirstColumn = false,
  toolbarExtra,
}) => {
  const { t } = useTranslation();
  const [filteredTableData, setFilteredTableData] = useState(datasource);
  const [viewState, setViewState] = useState({
    columns: columns,
    source: null,
    rows: null,
    headings: [] as StockTotal[],
  });

  const { enableVtTwo } = useFlags();
  const PRODUCT_REF = useMemo(() => {
    return (enableVtTwo ? 'lwin18' : 'id') as keyof BaseProduct;
  }, [enableVtTwo]);

  const onTableRowClick = (row: TableRow, openSlideout?: SortAndFilterLayoutContextType['openSlideout']) => {
    const newProduct = datasource.find((x: BaseProduct) => `${x[PRODUCT_REF]}` === row.id);
    if (openSlideout) openSlideout(newProduct ?? ({} as Product));
  };

  const buildTableRows = (dataSet: Product[]) => {
    return (dataSet || []).map((report) => {
      return {
        ...buildTableRow({ ...report }, columns || [], `${report[PRODUCT_REF]}`, 'divide-x-0'),
      };
    });
  };

  const updateViewState = (key: string, data: unknown) => {
    setViewState((vs) => ({
      ...vs,
      [key]: data,
    }));
  };

  const refreshBalances = (tableData: Product[]) => {
    let results = [] as StockTotal[];
    if (refreshTotals) results = refreshTotals(tableData);
    updateViewState('headings', [...results]);
  };

  const onFilter = (filteredData: Product[], newTabState?: TabState | null) => {
    if (onStateChange) onStateChange(newTabState || null);
    setFilteredTableData(filteredData);
    updateViewState('rows', buildTableRows(filteredData));
    refreshBalances(filteredData);
  };

  useEffect(() => {
    updateViewState('source', datasource);
  }, [datasource]);

  // When the column set changes (e.g. the per-case / totals view toggle flips
  // column visibility), refresh both the columns and the rendered rows so the
  // cells pick up the new visibility.
  useEffect(() => {
    updateViewState('columns', columns);
    updateViewState('rows', buildTableRows(filteredTableData));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns]);

  const errorText = useMemo(() => {
    if (error) {
      if (error instanceof ApolloError) return error.message;

      if (error.message) return error.message;

      if (typeof error === 'string') return error;
    }
    return '';
  }, [error]);

  // Totals summary (Portfolio Valuation / purchase price / P&L). Rendered as the layout `head`
  // — above the toolbar — so the toolbar sits directly on top of the table. Only used on the
  // default (no custom `children`) path; callers that supply their own body render their own head.
  const renderSummaryHead = () =>
    !loading && viewState.rows && (viewState.rows as TableRow[]).length > 0 ? (
      <div className="px-5 pt-1">
        <div className="flex flex-wrap  pb-1 bg-white dark:bg-slate-800 rounded-md border border-gray-100 dark:border-slate-700">
          {viewState.headings.map((heading, index) => (
            <div className="flex flex-col mr-[56px] mb-5" key={`${heading.title}-${index}`}>
              <span className="text-sm sm:text-14 mb-[4px] text-gray-600 dark:text-gray-400">{heading.title}</span>
              <span
                className="text-md flex items-end text-gray-900 dark:text-white"
                style={{ color: heading.color ? heading.color : undefined }}
              >
                {`${heading.text}`}{' '}
                <span className="text-14 ml-1">{`${heading.additionalText ? `${heading.additionalText}` : ''}`}</span>
              </span>
            </div>
          ))}
        </div>
      </div>
    ) : null;

  return (
    <SortAndFilterLayout
      head={children ? undefined : renderSummaryHead}
      selectedPortfolioId={selectedPortfolioId}
      disableExport={disableExport}
      disableSort={disableSort}
      searchQueryIn={searchQueryIn}
      filterPanelContainerClassName={filterPanelContainerClassName}
      tabState={tabState}
      compId={id}
      showCompactSwitch={showCompactSwitch}
      moreSelectorTemplate={moreSelectorTemplate}
      onFilter={onFilter}
      loading={!viewState.source || loading}
      columns={columns}
      filterConfigure={filterConfigure}
      isCardViewDefault={isCardViewDefault}
      sortByOptions={sortByOptions}
      products={viewState.source || []}
      defaultSortBy={defaultSortBy}
      filterOverrides={filterOverrides}
      totalCount={totalCount}
      toolbarExtra={toolbarExtra}
      slideoutContent={(
        productSelected: Product,
        timestamp: number,
        onClose?: () => void,
        setTitle?: (title: string) => void,
        isDetailsFetched?: boolean,
      ) => (
        <ProductSlideout
          isDetailsFetched={isDetailsFetched}
          setTitle={setTitle}
          product={productSelected}
          timestamp={timestamp}
          onClose={onClose}
        />
      )}
    >
      <SortAndFilterLayoutContext.Consumer>
        {(context) => {
          return children ? (
            <>{children(context)}</>
          ) : (
            <div className="flex flex-1 px-5 pb-5 w-full h-full flex-col mt-5">
              <div className="justify-center items-center bg-white dark:bg-slate-800 rounded-md flex-1 h-full">
                {errorText && <div>{errorText}</div>}
                {!errorText &&
                  (loading || !viewState.source ? (
                    <div className="p-4">
                      <WineCardListSkeleton count={6} />
                    </div>
                  ) : !viewState.rows ? null : (viewState.rows as TableRow[]).length === 0 ? (
                    <div className="animate-[fade-in_5s_ease-out]">
                      <SharedTemplate
                        showButton={false}
                        displayTextKeys={NoResultsTextKeys}
                        translationKey="common:noResult"
                        imageSrc={noResultImage}
                        title={
                          <div className="flex items-center gap-3">
                            <span className="text-20 font-medium ">{t('common:noResult.title')}</span>
                          </div>
                        }
                      />
                    </div>
                  ) : context?.cardLayoutConfig?.show ? (
                    <DetailCard
                      tableRows={viewState.rows || []}
                      dateFormat={dateFormat}
                      filteredItems={filteredTableData}
                      isCompact={context?.cardLayoutConfig?.isCompact}
                      onItemClick={(row) =>
                        disableTableClick ? null : onTableRowClick(row as TableRow, context!.openSlideout)
                      }
                    />
                  ) : (
                    <ColumnSortTable
                      sortIsAsc={sortIsAsc}
                      defaultSortKey={defaultSortKey}
                      scrollContained={scrollContained}
                      stickyFirstColumn={stickyFirstColumn}
                      updateDataSource={(data) => {
                        onFilter(data);
                      }}
                      dataSource={filteredTableData}
                      columns={viewState.columns || []}
                      rows={viewState.rows || []}
                      isFadeOnScrollDisable={true}
                      onTableEvent={(row) =>
                        disableTableClick ? null : onTableRowClick(row as TableRow, context!.openSlideout)
                      }
                    />
                  ))}
              </div>
            </div>
          );
        }}
      </SortAndFilterLayoutContext.Consumer>
    </SortAndFilterLayout>
  );
};

export default StockTab;
